import { Formik, Form } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import RowLoader from "../UI/RowLoader";
import {
  ARABIC_REGEX,
  E_MONEY_VALUE,
  ENGLISH_REGEX,
} from "../../constants/globals";
import * as Yup from "yup";
import Swal from "sweetalert2";
import PackageStepOne from "./AddOrEditPackageSteps/PackageStepOne";
import PackageStepTwo from "./AddOrEditPackageSteps/PackageStepTwo";
import PackageStepThree from "./AddOrEditPackageSteps/PackageStepThree";
import { useState } from "react";
import { createNewPackage, updatePackageData } from "../../apis";

const AddOrEditPackage = ({ dataToEdit, forEdit = false, coursesData }) => {
  const { packageId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Initial form values
  const initialValues = !forEdit
    ? {
        // step 1
        package_category: "",
        assigned_to: "",
        // default value fixed => 1
        package_type: "fixed",
        no_of_courses: "1",
        package_title_english: "",
        package_title_arabic: "",
        package_description_english: "",
        package_description_arabic: "",

        // step 2
        registration_price: "",
        courses_price: "",
        total_price_em: "",
        total_price_eg: "",
        is_pv_active: "1",
        is_pv_upliner_active: "1",
        value_of_direct_comission: "",
        activate_now: "1",
        installment_fees: "",
        // step 3
        courses: Array(1).fill({
          course_name: "",
          course_price: "0",
          pv_upliner: "0",
        }),
      }
    : {
        // step 1
        package_category: dataToEdit?.category_id,
        assigned_to: dataToEdit?.client_category_id,
        // default value fixed => 1
        package_type: dataToEdit?.package_type,
        no_of_courses: dataToEdit?.no_of_courses,
        package_title_english: dataToEdit?.package_name_english,
        package_title_arabic: dataToEdit?.package_name_arabic,
        package_description_english: dataToEdit?.package_description_english,
        package_description_arabic: dataToEdit?.package_description_arabic,
        // step 2
        registration_price: +dataToEdit?.registration_price,
        courses_price: parseInt(dataToEdit?.courses_price) || 0,
        total_price_em: parseInt(dataToEdit.total_price_em || 0),
        total_price_eg:
          parseInt(dataToEdit?.total_price_em || 0) * E_MONEY_VALUE,
        is_pv_active: dataToEdit?.is_pv_active,
        is_pv_upliner_active: dataToEdit?.is_pv_upliner_active ?? "",
        value_of_direct_comission: dataToEdit?.value_of_direct_comission,
        activate_now: dataToEdit?.activate_now,
        installment_fees: dataToEdit?.installment_fees,
        // step 3
        courses: coursesData || [],
      };

  // Validation schema using Yup
  const StepOneSchema = Yup.object().shape({
    // step 1
    package_category: Yup.string().required("Required"),
    assigned_to: Yup.string().required("Required"),
    package_type: Yup.string().required("Required"),
    no_of_courses: Yup.string().required("Required"),
    package_title_english: Yup.string()
      .matches(ENGLISH_REGEX, "Only English letters are allowed")
      .min(2, "Too Short!")
      .max(50, "Too Long!")
      .required("Required"),
    package_title_arabic: Yup.string()
      .matches(ARABIC_REGEX, "Only English letters are allowed")
      .min(2, "Too Short!")
      .max(50, "Too Long!")
      .required("Required"),
    package_description_english: Yup.string()
      .matches(ENGLISH_REGEX, "Only English letters are allowed")
      .min(2, "Too Short!")
      .max(200, "Too Long!")
      .required("Required"),
    package_description_arabic: Yup.string()
      .matches(ARABIC_REGEX, "Only Arabic letters are allowed")
      .min(2, "Too Short!")
      .max(200, "Too Long!")
      .required("Required"),

    // step 2
    registration_price: Yup.number()
      .required("Required")
      .min(0, "Must be 0 or a positive number"),
    courses_price: Yup.number()
      .min(0, "Must be 0 or a positive number")
      .required("Required"),
    installment_fees: Yup.number()
      .min(0, "Must be 0 or a positive number")
      .required("Required"),
    is_pv_active: Yup.string().required("Required"),
    is_pv_upliner_active: Yup.string().required("Required"),
    value_of_direct_comission: Yup.number()
      .required("Required")
      .positive("Must be a positive number"),
    activate_now: Yup.string().required("Required"),
  });

  // Form submit handler
  const handleSubmit = (values) => {
    const coursesPrice = +values.courses_price;
    const totalCoursesPriceEntered = values.courses.reduce(
      (total, course) => total + Number(course.course_price || 0),
      0
    );

    if (coursesPrice !== totalCoursesPriceEntered) {
      return Swal.fire({
        icon: "warning",
        title: "Sorry!",
        text: "Courses price must match the sum of entered course prices",
      });
    }

    const formattedCourses = values.courses.map((course, index) => ({
      course_id: course.course_name,
      e_money: course.course_price,
      weight: course.pv_upliner,
    }));

    const handleData = {
      package_name_english: values.package_title_english,
      package_name_arabic: values.package_title_arabic,
      package_type: values.package_type,
      package_description_english: values.package_description_english,
      package_description_arabic: values.package_description_arabic,
      no_of_courses: values.no_of_courses,
      registration_price: values.registration_price,
      courses_price: values.courses_price,
      total_price_em: values.total_price_em,
      activate_now: +values.activate_now,
      is_pv_active: +values.is_pv_active,
      is_pv_upliner_active: +values.is_pv_upliner_active,
      value_of_direct_comission: values.value_of_direct_comission,
      category_id: values.package_category,
      client_category_id: values.assigned_to,
      installment_fees: values.installment_fees,
      courses: formattedCourses,
    };

    setIsLoading(true);
    if (!forEdit) {
      createNewPackage(handleData)
        .then((response) => {
          Swal.fire({
            icon: "success",
            title: "Package created successfully",
            showConfirmButton: true,
            timer: 3500,
          }).then(() => {
            navigate(-1);
          });
        })
        .catch((error) => {
          console.log(error);
          Swal.fire({
            icon: "error",
            title: "Sorry!",
            text: error.response.data.message.join(", "),
            showConfirmButton: true,
          });
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      updatePackageData(packageId, handleData)
        .then((response) => {
          Swal.fire({
            icon: "success",
            title: "Package updated successfully",
            showConfirmButton: true,
            timer: 3500,
          }).then(() => {
            navigate(-1);
          });
        })
        .catch((error) => {
          console.log(error);
          Swal.fire({
            icon: "error",
            title: "Sorry!",
            text: error.response.data.message.join(", "),
            showConfirmButton: true,
          });
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  return (
    <>
      <p className="tw-text-[#BF0404] tw-text-end tw-mb-1">
        * means this field is required
      </p>

      <Formik
        initialValues={initialValues}
        validationSchema={StepOneSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({
          errors,
          touched,
          handleBlur,
          handleChange,
          setFieldValue,
          values,
        }) => (
          <Form className="d-flex flex-column">
            <PackageStepOne
              errors={errors}
              handleBlur={handleBlur}
              handleChange={handleChange}
              setFieldValue={setFieldValue}
              touched={touched}
              values={values}
            />
            <PackageStepTwo
              errors={errors}
              handleBlur={handleBlur}
              handleChange={handleChange}
              setFieldValue={setFieldValue}
              touched={touched}
              values={values}
            />
            <PackageStepThree
              errors={errors}
              handleBlur={handleBlur}
              handleChange={handleChange}
              setFieldValue={setFieldValue}
              touched={touched}
              values={values}
              numOfCourses={values.no_of_courses || 1}
              coursesData={coursesData}
            />
            <button
              type="submit"
              className="btn btn-primary ml-auto !tw-w-[150px]"
            >
              {isLoading ? <RowLoader paddingStyle="tw-py-0" /> : "Submit"}
            </button>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default AddOrEditPackage;
